// =============================================================================
// schemaBackfillService — dogenerowanie Schema.org JSON-LD do JUŻ opublikowanych
// artykułów WP, bez ruszania treści.
//
// Mądry mechanizm:
//   1. graf budujemy z danych DB (generateSchemasForNode — reuse),
//   2. wstrzykujemy go w ŻYWĄ treść pobraną z WP (fetchPost context=edit → content.raw),
//      dzięki czemu zachowujemy ewentualne ręczne edycje w WP,
//   3. injectSchemasIntoHtml jest idempotentny (wycina stary <script ld+json>, wstawia nowy),
//   4. updatePost({content}) zapisuje — podmieniamy WYŁĄCZNIE blok schemy.
//
// dateModified = datePublished (decyzja: backfill to zabieg techniczny, nie edycja →
// zero sygnału "świeżo edytowane" dla Google).
//
// ZERO zmian w publishQueueService / generateSchemasForNode / resolveDates.
// =============================================================================
import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";
import {
  generateSchemasForNode,
  injectSchemasIntoHtml,
  logSchemaInjection,
} from "./schemaGenerator";
import type { SchemaBlogPosting, SchemaWebPage } from "./types";
import { logPublic } from "@/services/contentEngine/contentLogger";

export interface BackfillResult {
  nodeId: string;
  status: "ok" | "skipped" | "error";
  reason?: string;
  schemaCount?: number;
}

export interface WpCreds {
  url: string;
  username: string;
  password: string;
}

/**
 * Dogeneruj/odśwież schemę dla jednego opublikowanego node'a.
 * Wymaga ustawionych WP creds (przekazywane z runnera, restore-pattern wewnątrz).
 */
export async function reinjectSchemaForNode(
  node: { id: string; slug?: string | null },
  content: { wp_post_id?: number | null },
  creds: WpCreds,
): Promise<BackfillResult> {
  // 1. Graf z DB (reuse — zwraca graph:null gdy website.schema_enabled=false / content not ready)
  const payload = await generateSchemasForNode(node.id);
  if (!payload.graph || payload.graph["@graph"].length === 0) {
    const reason = payload.warnings[0] || payload.errors[0] || "brak grafu schemy";
    return {
      nodeId: node.id,
      status: payload.errors.length > 0 ? "error" : "skipped",
      reason,
    };
  }

  // 2. dateModified = datePublished (post-process TYLKO tutaj, nie w resolveDates)
  for (const g of payload.graph["@graph"]) {
    if (g["@type"] === "BlogPosting") {
      const bp = g as SchemaBlogPosting;
      if (bp.datePublished) bp.dateModified = bp.datePublished;
    } else if (g["@type"] === "WebPage") {
      const wp = g as SchemaWebPage;
      if (wp.datePublished) wp.dateModified = wp.datePublished;
    }
  }

  // 3. Restore-pattern credentials (jak contentAuditService)
  const prev = wordpressService.getCredentials();
  wordpressService.setCredentials(creds);
  try {
    let wpPostId = content.wp_post_id ?? null;
    if (!wpPostId && node.slug) {
      wpPostId = await wordpressService.findPostIdBySlug(node.slug);
    }
    if (!wpPostId) {
      return { nodeId: node.id, status: "skipped", reason: "brak wp_post_id (nie znaleziono po slug)" };
    }

    // 4. Pobierz ŻYWĄ treść z WP (zachowuje edycje WP)
    const live = await wordpressService.fetchPost(wpPostId);
    if (!live.success || !live.data) {
      return { nodeId: node.id, status: "error", reason: `WP fetch: ${live.error || "brak danych"}` };
    }
    const liveHtml: string =
      live.data?.content?.raw ?? live.data?.content?.rendered ?? "";
    if (!liveHtml) {
      return { nodeId: node.id, status: "error", reason: "WP zwrócił pustą treść" };
    }

    // 5. Wstrzyknij schemę (idempotent strip+add) i zapisz tylko content
    const newHtml = injectSchemasIntoHtml(liveHtml, payload.graph);
    const upd = await wordpressService.updatePost(wpPostId, { content: newHtml });
    if (!upd.success) {
      return { nodeId: node.id, status: "error", reason: `WP update: ${upd.error || "fail"}` };
    }

    // 6. Audit log (jak przy publish)
    await logSchemaInjection(node.id, payload, liveHtml.length, newHtml.length, wpPostId);

    logPublic("SCHEMA-BACKFILL", `Node ${node.id}: wstrzyknięto @graph (${payload.graph["@graph"].length} węzłów) → WP post ${wpPostId}`);
    return { nodeId: node.id, status: "ok", schemaCount: payload.graph["@graph"].length };
  } catch (e) {
    return { nodeId: node.id, status: "error", reason: e instanceof Error ? e.message : "wyjątek" };
  } finally {
    if (prev) wordpressService.setCredentials(prev);
  }
}

/**
 * Mapa nodeId → czy MA już schemę (z schema_injection_logs, niepusty graf).
 * Tani sygnał z DB (bez pukania do WP) — do filtrowania "tylko brakujące" + badge.
 */
export async function fetchSchemaStatusMap(nodeIds: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>(); // nodeId → injected_at (ISO) najnowszy
  if (nodeIds.length === 0) return map;
  try {
    const { data } = await (supabase as any)
      .from("schema_injection_logs")
      .select("node_id, injected_at, schemas_injected")
      .in("node_id", nodeIds)
      .order("injected_at", { ascending: false });
    for (const row of (data || []) as Array<{ node_id: string; injected_at: string; schemas_injected: unknown }>) {
      if (map.has(row.node_id)) continue; // pierwszy = najnowszy
      const arr = row.schemas_injected as unknown[];
      if (Array.isArray(arr) && arr.length > 0) map.set(row.node_id, row.injected_at);
    }
  } catch {
    /* brak logów = wszystko traktujemy jako bez schemy */
  }
  return map;
}

/**
 * Pobierz WP creds + schema_enabled dla domeny (po websiteId).
 */
export async function fetchWebsiteForBackfill(websiteId: string): Promise<{
  creds: WpCreds | null;
  schemaEnabled: boolean;
  name: string | null;
}> {
  const { data } = await (supabase as any)
    .from("websites")
    .select("url, login, password, api_key, schema_enabled, name")
    .eq("id", websiteId)
    .maybeSingle();
  if (!data) return { creds: null, schemaEnabled: false, name: null };
  const password = data.api_key || data.password;
  const creds: WpCreds | null =
    data.url && data.login && password
      ? { url: String(data.url).replace(/\/$/, ""), username: data.login, password }
      : null;
  return { creds, schemaEnabled: data.schema_enabled === true, name: data.name ?? null };
}
